<template>
  <div class="space-y-4">
    <BaseCard>
      <BaseCardBody clear-padding>
        <div class="px-4 py-5 sm:px-6 border-b">
          <dt class="text-sm font-medium text-gray-800 space-x-2">
            <span>Lines</span>
          </dt>
        </div>
        <div class="px-4 py-5 sm:px-6 border-b">
          <dt class="text-sm font-medium text-gray-800 flex justify-between">
            <div class="space-x-2">
              <BaseButton type="button" primary small @click="insertLine"> Add Line </BaseButton>
              <AddProduct
                :default-vat-rate="defaultVatRate"
                :parent-offer-line-id="parentOfferLineId"
                @insert="insertLines"
              />
            </div>
          </dt>
        </div>
        <table class="table-auto w-full">
          <thead>
            <tr class="text-sm font-medium text-gray-500 border-b">
              <th class="pl-4 pr-2 sm:pl-6 sm:pr-4 py-5 w-28 text-left">Quantity</th>
              <th class="py-5 whitespace-nowrap" style="width: 1%" />
              <th class="px-2 sm:px-4 py-5 text-left">Description</th>
              <th class="px-2 sm:px-4 py-5">Price per unit</th>
              <th class="px-2 sm:px-4 py-5 text-right">Net amount</th>
              <th class="px-2 sm:px-4 py-5">Vat rate</th>
              <th class="px-2 sm:px-4 py-5 text-right">Vat amount</th>
              <th class="px-2 sm:px-4 py-5 text-right">Total</th>
              <th class="pr-4 pl-2 sm:pr-6 sm:pl-4 py-5 w-px" />
            </tr>
          </thead>
          <tbody v-if="parentOfferLine === null">
            <template v-for="(offerLine, idx) in rootOfferLines">
              <TableLine
                v-if="isChild(offerLine)"
                :key="idx"
                :offer-line="offerLine"
                :has-children="hasChildren(offerLine)"
                @update="updateLine"
                @remove="removeLine"
                @zoom-in="parentOfferLineId = $event"
              />
            </template>
            <TableTotalLine :lines="offerLines" />
          </tbody>
          <template v-else>
            <tbody>
              <TableLine
                key="parentOfferLine"
                :offer-line="parentOfferLine"
                is-current
                @update="updateLine"
                @remove="removeLine"
                @zoom-out="zoomOut"
              />
            </tbody>
            <tbody>
              <template v-for="(offerLine, idx) in parentOfferLineChildren">
                <TableLine
                  v-if="isChild(offerLine)"
                  :key="idx"
                  :offer-line="offerLine"
                  :has-children="hasChildren(offerLine)"
                  is-focused
                  @update="updateLine"
                  @remove="removeLine"
                  @zoom-in="parentOfferLineId = $event"
                />
              </template>
            </tbody>
            <tbody>
              <TableTotalLine :lines="[parentOfferLine, ...parentOfferLineChildren]" />
            </tbody>
          </template>
        </table>
      </BaseCardBody>
      <BaseCardFooter v-if="offerLines.length === 0">
        <BaseFormInlineMessage danger> You must add at least one line </BaseFormInlineMessage>
      </BaseCardFooter>
    </BaseCard>
  </div>
</template>

<script>
import { uniqueId, omit } from 'lodash-es';
import AddProduct from './AddProduct.vue';
import TableLine from './TableLine.vue';
import TableTotalLine from './TableTotalLine.vue';

export default {
  components: {
    AddProduct,
    TableLine,
    TableTotalLine,
  },
  props: {
    defaultVatRate: {
      type: Number,
      default: 0,
    },
    offerLines: {
      type: Array,
      default: () => [],
    },
    canEditLines: Boolean,
  },
  data() {
    return {
      parentOfferLineId: null,
    };
  },
  computed: {
    rootOfferLines() {
      return this.offerLines.filter(
        (offerLine) => offerLine.parentOfferLineId === null || offerLine._parentOfferLineId === null
      );
    },
    parentOfferLine() {
      return (
        this.offerLines.find(
          (offerLine) =>
            offerLine.uuid === this.parentOfferLineId || offerLine._uuid === this.parentOfferLineId
        ) || null
      );
    },
    parentOfferLineChildren() {
      if (!this.parentOfferLine) {
        return [];
      }

      return this.offerLines.filter(
        (offerLine) =>
          offerLine.parentOfferLineId === this.parentOfferLineId ||
          offerLine._parentOfferLineId === this.parentOfferLineId ||
          null
      );
    },
    toRemove() {
      return this.offerLines.filter((offerLine) => offerLine.remove);
    },
  },
  methods: {
    zoomOut() {
      this.parentOfferLineId =
        this.parentOfferLine?.parentOfferLineId || this.parentOfferLine?._parentOfferLineId || null;
    },
    hasChildren(offerLine) {
      return this.offerLines.some((o) => {
        if ('uuid' in offerLine) {
          return o.parentOfferLineId === offerLine.uuid;
        }

        return o._parentOfferLineId === offerLine._uuid;
      });
    },
    isChild(offerLine) {
      return (
        offerLine.parentOfferLineId === this.parentOfferLineId ||
        offerLine._parentOfferLineId === this.parentOfferLineId
      );
    },
    insertLines(newOfferLines) {
      this.$emit('change', [...this.offerLines, ...newOfferLines]);
    },
    insertLine() {
      const prefix = 'offer_line_';
      const id = uniqueId(prefix);

      const offerLine = {
        _uuid: id,
        description: '',
        pricePerUnit: '',
        quantity: 1,
        vatRate: this.defaultVatRate,
        productId: null,
        ...(this.parentOfferLineId === null || this.parentOfferLineId.indexOf(prefix) === -1
          ? {
              parentOfferLineId: this.parentOfferLineId,
            }
          : { _parentOfferLineId: this.parentOfferLineId }),
      };

      this.$emit('change', [...this.offerLines, offerLine]);
    },
    findOfferLineIndex(offerLine) {
      const index = this.offerLines.findIndex((o) => {
        if ('uuid' in offerLine) {
          return o.uuid === offerLine.uuid;
        }

        return o._uuid === offerLine._uuid;
      });

      return index;
    },
    updateLine(offerLine) {
      const index = this.findOfferLineIndex(offerLine);

      if (index === -1) {
        return;
      }

      this.$emit(
        'change',
        Object.values({ ...omit(this.offerLines, [index]), [index]: offerLine })
      );
    },
    removeLine(offerLine) {
      this.markRemoved(offerLine);

      const offerLinesToKeep = this.offerLines.filter((o) => Boolean(o.remove) === false);

      this.$emit('change', [...offerLinesToKeep]);
    },
    markRemoved(offerLine) {
      const index = this.findOfferLineIndex(offerLine);
      const lineToRemove = this.offerLines[index];

      if (!lineToRemove || lineToRemove.remove) {
        return;
      }

      lineToRemove.remove = true;

      const { uuid, _uuid: tempUuid } = lineToRemove;
      const subjectUuid = uuid || tempUuid;

      this.offerLines.forEach((o) => {
        const { parentOfferLineId, _parentOfferLineId: tempParentOfferLineId } = o;
        const targetUuid = parentOfferLineId || tempParentOfferLineId;

        if (subjectUuid === targetUuid) {
          this.markRemoved(o);
        }
      });
    },
  },
};
</script>
